import { doc, getDoc } from 'firebase/firestore';
import { db } from './firebase';

/**
 * Puts the platform owner's head tags (analytics, pixels, verification) into
 * the document. Read from the public /platform/site document once per page
 * load, before sign-in, so a visitor on the landing page is counted too.
 *
 * Markup parsed into an inert document does not run its scripts, so each
 * <script> is rebuilt by hand; everything else is cloned across as is.
 */
export async function injectHeadTags(): Promise<void> {
  try {
    const snap = await getDoc(doc(db, 'platform', 'site'));
    const html = String(snap.data()?.headTags ?? '').trim();
    if (!html) return;

    const parsed = new DOMParser().parseFromString(`<!doctype html><html><head>${html}</head></html>`, 'text/html');
    const nodes = [...Array.from(parsed.head.childNodes), ...Array.from(parsed.body.childNodes)];
    for (const node of nodes) {
      if (node instanceof HTMLScriptElement) {
        const script = document.createElement('script');
        for (const attr of Array.from(node.attributes)) script.setAttribute(attr.name, attr.value);
        script.textContent = node.textContent;
        document.head.appendChild(script);
      } else if (node.nodeName === 'NOSCRIPT') {
        document.body.appendChild(document.importNode(node, true));
      } else {
        document.head.appendChild(document.importNode(node, true));
      }
    }
  } catch {
    // A tracker that fails to load must never stop the product from loading.
  }
}
